import React from 'react';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-gray-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="prose prose-lg text-gray-600 max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="mb-4">
                By accessing and using EduLearn ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
              <p>
                These Terms of Service ("Terms") govern your use of our website located at edulearn.com and any related services provided by EduLearn.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="mb-4">
                EduLearn is an online learning platform that provides:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Access to online courses and educational content</li>
                <li>Tools for instructors to create and manage courses</li>
                <li>Student enrollment and progress tracking</li>
                <li>Community features for learners and instructors</li>
                <li>Support and customer service</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. User Accounts</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Account Creation</h3>
              <p className="mb-4">
                To access certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Account Security</h3>
              <p className="mb-4">
                You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. You agree not to disclose your password to any third party and to take sole responsibility for any activities or actions under your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. User Conduct</h2>
              <p className="mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Upload or transmit viruses or other malicious code</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Use the Service for any commercial purpose without our express written consent</li>
                <li>Impersonate any person or entity</li>
                <li>Harass, abuse, or harm other users</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Course Content and Intellectual Property</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Instructor Content</h3>
              <p className="mb-4">
                Instructors retain ownership of the content they create and upload to the platform. By uploading content, instructors grant EduLearn a license to host, display, and distribute their content to students enrolled in their courses.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Platform Content</h3>
              <p className="mb-4">
                The Service and its original content, features, and functionality are and will remain the exclusive property of EduLearn and its licensors. The Service is protected by copyright, trademark, and other laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Payment and Refunds</h2>
              <p className="mb-4">
                Currently, all courses on EduLearn are free. In the future, if paid courses are introduced:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>All payments are processed securely through our payment partners</li>
                <li>Course prices are clearly displayed before enrollment</li>
                <li>Refunds may be available within 30 days of purchase, subject to our refund policy</li>
                <li>We reserve the right to change pricing at any time with notice</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Privacy and Data Protection</h2>
              <p className="mb-4">
                Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices regarding the collection and use of your personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Disclaimers and Limitations</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Service Availability</h3>
              <p className="mb-4">
                The Service is provided on an "as is" and "as available" basis. We do not guarantee that the Service will be uninterrupted, secure, or error-free.
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Content Accuracy</h3>
              <p className="mb-4">
                While we strive to ensure the quality of content on our platform, we do not guarantee the accuracy, completeness, or usefulness of any information provided by instructors or other users.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Termination</h2>
              <p className="mb-4">
                We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
              </p>
              <p>
                If you wish to terminate your account, you may simply discontinue using the Service or contact us to delete your account.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
              <p>
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Governing Law</h2>
              <p>
                These Terms shall be interpreted and governed by the laws of the United States, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Information</h2>
              <p className="mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="mb-2"><strong>Email:</strong> legal@edulearn.com</p>
                <p className="mb-2"><strong>Address:</strong> 123 Learning Street, Education City, EC 12345, United States</p>
                <p><strong>Phone:</strong> +1 (555) 123-4567</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
